import type { RefObject } from 'react'
import { useEffect, useRef } from 'react'

const defaultEvents = ['mouseup', 'touchend'] as string[]

export function useClickOutside(
  ref: RefObject<HTMLElement | null>,
  onClickAway: (event: MouseEvent | TouchEvent) => void,
  options?: {
    ignoreRefs?: RefObject<HTMLElement | null>[]
    events?: readonly string[]
  },
) {
  const { ignoreRefs = [], events = defaultEvents } = options ?? {}
  const savedCallback = useRef(onClickAway)

  useEffect(() => {
    savedCallback.current = onClickAway
  }, [onClickAway])

  useEffect(() => {
    const handler = (event: Event) => {
      const el = ref.current
      const target = event.target

      if (!(target instanceof Node)) {
        return
      }

      for (const ignoreRef of ignoreRefs) {
        if (ignoreRef.current?.contains(target)) {
          return
        }
      }

      if (el && !el.contains(target)) {
        if (event instanceof MouseEvent || event instanceof TouchEvent) {
          savedCallback.current(event)
        }
      }
    }

    events.forEach(eventName => document.addEventListener(eventName, handler))

    return () => {
      events.forEach(eventName =>
        document.removeEventListener(eventName, handler),
      )
    }
  }, [events, ref, ignoreRefs])
}
